import React, { ComponentProps } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AdminToolSearchResultItem } from 'types/admin-tools/AdminToolSearchResultItem';
import AdminSearchToolsQueryHelper, { PAGE_SIZES } from './AdminSearchToolsQueryHelper';
import AdminSearchResultsGrid from 'components/admin/search/AdminSearchResultsGrid';
import { EpdLinks } from 'routes/EpdRoutes';
import Tooltip from 'components/controls/Tooltip';
import { Column } from 'primereact/column';
import getToolTypeLabel from 'util/getToolTypeLabel';
import ToolStatusBadge from 'components/admin/search/ToolStatusBadge';
import { ToolHeadState } from 'types/tools/ToolHeadState';
import { PagedResult } from 'types/search-common/PagedResult';
import { ToolOwnerCompany } from 'types/tools/ToolOwnerCompany';

interface Props {
  data: PagedResult<AdminToolSearchResultItem>;
  showLoading: boolean;
}

const AdminSearchToolsResultsTable: React.FC<Props> = ({ data: { totalCount, items }, showLoading }) => {
  const location = useLocation();
  const currentFilter = AdminSearchToolsQueryHelper.parse(location.search);
  const history = useHistory();

  const onPage: ComponentProps<typeof AdminSearchResultsGrid>['onPageChange'] = (event) => {
    const newQuery = AdminSearchToolsQueryHelper.stringify({
      ...currentFilter,
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    });
    history.push(location.pathname + newQuery);
    document.getElementById('searchResults')?.scrollIntoView();
  };

  const renderLink = (data: AdminToolSearchResultItem, additionalInfo?: { tooltip?: string }) => (
    // We want the entire cell to be clickable (including padding areas).
    <OverlayEntireCell>
      <CellLink to={EpdLinks.editTool(data.id)} aria-label={`See ${data.name}`} />
      {additionalInfo?.tooltip && <Tooltip position="title">{additionalInfo.tooltip}</Tooltip>}
    </OverlayEntireCell>
  );

  const renderLinkToCompanySettings = (data: ToolOwnerCompany, additionalInfo?: { tooltip?: string }) => (
    <OverlayEntireCell>
      <CellLink to={EpdLinks.manageCompany(data.id)} aria-label={`See ${data.name}`} />
      {additionalInfo?.tooltip && <Tooltip position="title">{additionalInfo.tooltip}</Tooltip>}
    </OverlayEntireCell>
  );

  return (
    <GridWithTaintedRows
      id="searchResults"
      style={{ scrollMarginTop: 100 }}
      value={items}
      totalRecords={totalCount}
      pageIndex={currentFilter.pageNumber - 1}
      pageSize={currentFilter.pageSize}
      onPageChange={onPage}
      pageSizeOptions={PAGE_SIZES}
      loading={showLoading}
      tableStyle={{ tableLayout: 'fixed' }}
      rowClassName={(tool: AdminToolSearchResultItem) => ({
        'tainted-row': tool.headState === ToolHeadState.INACTIVE,
      })}
    >
      <Column
        field="toolNumber"
        header="Tool ID"
        style={{ width: 100 }}
        body={(data: AdminToolSearchResultItem) => (
          <EllipsedCell>
            {renderLink(data)}
            <EllipsedText>{data.toolNumber.toString().padStart(3, '0')}</EllipsedText>
          </EllipsedCell>
        )}
      />
      <Column
        field="name"
        header="Tool name"
        body={(data: AdminToolSearchResultItem) => (
          <EllipsedCell>
            {renderLink(data, { tooltip: data.name })}

            <EllipsedText>{data.name || 'Unnamed tool'}</EllipsedText>
          </EllipsedCell>
        )}
      />
      <Column
        field="toolType"
        header="Tool type"
        style={{ width: 220 }}
        body={(data: AdminToolSearchResultItem) => {
          const text = getToolTypeLabel(data.toolType);
          return (
            <EllipsedCell>
              {renderLink(data, { tooltip: text })}
              <EllipsedText>{text}</EllipsedText>
            </EllipsedCell>
          );
        }}
      />
      <Column
        field="toolOwnerCompany"
        header="Tool owner"
        style={{ width: 140 }}
        body={(data: AdminToolSearchResultItem) => {
          const text = data.toolOwnerCompany?.name ?? '';
          return (
            <ToolOwnerEllipsedCell>
              {data.toolOwnerCompany && renderLinkToCompanySettings(data.toolOwnerCompany, { tooltip: text })}
              <EllipsedText>{text}</EllipsedText>
            </ToolOwnerEllipsedCell>
          );
        }}
      />
      <Column
        field="verifierName"
        header="Verifier"
        style={{ width: 140 }}
        body={(data: AdminToolSearchResultItem) => {
          const text = data.verifierName ?? '';
          return (
            <EllipsedCell>
              {renderLink(data, { tooltip: text })}
              <EllipsedText>{text}</EllipsedText>
            </EllipsedCell>
          );
        }}
      />
      <Column
        field="validityDate"
        header="Validity Date"
        style={{ width: 140 }}
        body={(data: AdminToolSearchResultItem) => {
          const text = data.validityDate ?? '';
          return (
            <EllipsedCell>
              {renderLink(data, { tooltip: text })}
              <EllipsedText>{text}</EllipsedText>
            </EllipsedCell>
          );
        }}
      />
      <Column
        field="headState"
        header="Status"
        style={{ width: 140 }}
        body={(data: AdminToolSearchResultItem) => (
          <EllipsedCell>
            {renderLink(data)}
            <ToolStatusBadge toolHeadState={data.headState} />
          </EllipsedCell>
        )}
      />
    </GridWithTaintedRows>
  );
};

export default AdminSearchToolsResultsTable;

const GridWithTaintedRows = styled(AdminSearchResultsGrid)`
  & .p-datatable-tbody > tr.tainted-row {
    background-color: ${(props) => props.theme.colors.elementsTableRow};
  }

  & .p-datatable-tbody > tr.tainted-row > td:not(:last-child) {
    opacity: 0.5;
  }
`;

const EllipsedCell = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.2;
`;

const ToolOwnerEllipsedCell = styled(EllipsedCell)`
  &:hover {
    color: ${(props) => props.theme.colors.primaryGreen};
  }
`;

const EllipsedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
`;

const OverlayEntireCell = styled.div`
  td:has(&&) {
    position: relative;
  }
`;

const CellLink = styled(Link)`
  z-index: 0;
  position: absolute;
  inset: 0;
`;
